import React from "react";
import { IconLoader } from "@synthesis/shared-icons";
import { cn, mergeRefs } from "@synthesis/shared-utils";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Tooltip as Tooltip$1 } from "@base-ui/react/tooltip";
import { useQuery } from "@tanstack/react-query";
//#region src/Tooltip/index.tsx
const POINTER_LEAVE_TOOLTIP = 350;
const ALIGN_OFFSET = -12;
const SIDE_OFFSET = 5;
const DEFAULT_OPEN_DELAY = 700;
const sizeTextClasses = {
	lg: "text-lg",
	md: "text-base",
	sm: "text-sm"
};
const sizePaddingClasses = {
	lg: "p-3",
	md: "py-2 px-3",
	sm: "p-2"
};
function ArrowTooltipSvg({ theme, ...props }) {
	const fillMain = theme === "dark" ? "fill-neutrals-20" : "fill-white";
	const strokeBorder = theme === "dark" ? "stroke-neutrals-50" : "stroke-neutrals-90";
	return /* @__PURE__ */ jsxs("svg", {
		width: "20",
		height: "10",
		viewBox: "0 0 20 8",
		fill: "none",
		...props,
		children: [/* @__PURE__ */ jsx("path", {
			d: "M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8L2.13172 10L18.5349 10L18.5349 8C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z",
			className: fillMain
		}), /* @__PURE__ */ jsx("path", {
			d: "M2.13172 8C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8",
			className: strokeBorder,
			fill: "none",
			strokeWidth: 1
		})]
	});
}
const Tooltip = React.forwardRef(({ theme = "dark", title, description, children, triggerEvent = "mouse", alignOffset = ALIGN_OFFSET, sideOffset = SIDE_OFFSET, arrowSide = "left", arrowAlign = "start", delayDuration, disabled, size = "md", fetchData, fetchDataId, descriptionStyle, noMaxWidth = false, maxWidth, noArrow = false, ...rest }, ref) => {
	const leaveTimeout = React.useRef(void 0);
	const isClick = triggerEvent === "click";
	const [open, setOpen] = React.useState(isClick ? false : void 0);
	const { data, isFetching } = useQuery({
		queryKey: ["tooltip", fetchDataId],
		queryFn: ({ signal }) => fetchData?.(signal),
		enabled: Boolean(fetchData && open),
		retry: false,
		gcTime: 6e5,
		staleTime: 6e5,
		refetchOnWindowFocus: false
	});
	React.useEffect(() => {
		if (isClick) {
			setOpen(false);
			return;
		}
		setOpen(void 0);
		return () => {
			if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
		};
	}, [isClick]);
	const handleRootOpenChange = React.useCallback((nextOpen) => {
		if (fetchData) {
			setOpen(nextOpen);
			return;
		}
		if (isClick && !nextOpen) setOpen(false);
	}, [fetchData, isClick]);
	const handlePointerLeave = () => {
		if (isClick) leaveTimeout.current = setTimeout(() => setOpen(false), POINTER_LEAVE_TOOLTIP);
	};
	const handlePointerEnter = () => {
		if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
	};
	if (disabled) return children;
	const body = fetchData ? isFetching ? /* @__PURE__ */ jsx("div", {
		className: "text-sm",
		children: /* @__PURE__ */ jsx(IconLoader, { className: "size-6 animate-spin" })
	}) : /* @__PURE__ */ jsxs(Fragment, { children: [data?.title && /* @__PURE__ */ jsx("h3", {
		className: cn("m-0 break-words p-0 font-medium", sizeTextClasses[size]),
		children: data.title
	}), /* @__PURE__ */ jsx("div", {
		"data-cy": "tooltip-description",
		className: cn("break-words font-normal", sizeTextClasses[size]),
		style: descriptionStyle,
		children: data?.description || description
	})] }) : /* @__PURE__ */ jsxs(Fragment, { children: [title && /* @__PURE__ */ jsx("h3", {
		className: cn("m-0 break-words p-0 font-medium", sizeTextClasses[size]),
		children: title
	}), /* @__PURE__ */ jsx("div", {
		"data-cy": "tooltip-description",
		className: cn("break-words font-normal", sizeTextClasses[size]),
		style: descriptionStyle,
		children: description
	})] });
	return /* @__PURE__ */ jsxs(Tooltip$1.Root, {
		open,
		onOpenChange: fetchData || isClick ? handleRootOpenChange : void 0,
		children: [/* @__PURE__ */ jsx(Tooltip$1.Trigger, {
			delay: delayDuration ?? DEFAULT_OPEN_DELAY,
			closeDelay: isClick ? POINTER_LEAVE_TOOLTIP : void 0,
			closeOnClick: !isClick,
			render: (triggerProps) => {
				const childElement = children;
				return React.cloneElement(childElement, {
					...rest,
					...triggerProps,
					ref: mergeRefs(ref, triggerProps.ref, childElement.props.ref),
					onClick: (event) => {
						triggerProps.onClick?.(event);
						if (triggerEvent === "click") setOpen(true);
						childElement.props.onClick?.(event);
					}
				});
			}
		}), /* @__PURE__ */ jsx(Tooltip$1.Portal, { children: /* @__PURE__ */ jsx(Tooltip$1.Positioner, {
			className: "z-[9999]",
			side: arrowSide,
			align: arrowAlign,
			alignOffset: arrowAlign === "center" ? 0 : alignOffset,
			sideOffset,
			children: /* @__PURE__ */ jsxs(Tooltip$1.Popup, {
				"data-cy": "tooltip-content",
				onPointerLeave: handlePointerLeave,
				onPointerEnter: handlePointerEnter,
				className: cn("shadow-d z-[9999] flex max-h-[400px] flex-col gap-1 overflow-y-auto whitespace-normal rounded-lg border-2 font-sans", "[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:p-0.5", "[&::-webkit-scrollbar-track]:rounded-xl [&::-webkit-scrollbar-track]:bg-transparent", theme === "dark" ? "[&::-webkit-scrollbar-track:hover]:bg-[rgba(255,255,255,0.05)]" : "[&::-webkit-scrollbar-track:hover]:bg-[rgba(0,0,0,0.05)]", "[&::-webkit-scrollbar-thumb]:min-h-12 [&::-webkit-scrollbar-thumb]:rounded-xl [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent", theme === "dark" ? "[&::-webkit-scrollbar-thumb:hover]:bg-[rgba(255,255,255,0.3)] [&::-webkit-scrollbar-thumb]:bg-[rgba(255,255,255,0.2)]" : "[&::-webkit-scrollbar-thumb:hover]:bg-[rgba(0,0,0,0.3)] [&::-webkit-scrollbar-thumb]:bg-[rgba(0,0,0,0.2)]", "[&::-webkit-scrollbar-thumb]:bg-clip-padding", sizePaddingClasses[size], theme === "dark" ? "border-neutrals-50 bg-neutrals-20 text-white" : "border-neutrals-90 bg-white", maxWidth ? "max-w-[unset]" : !noMaxWidth ? "max-w-[240px]" : void 0),
				style: maxWidth ? { maxWidth } : void 0,
				children: [!noArrow && /* @__PURE__ */ jsx(Tooltip$1.Arrow, {
					className: cn("flex", "data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0", "data-[side=left]:right-[-13px] data-[side=left]:rotate-90", "data-[side=right]:left-[-13px] data-[side=right]:-rotate-90", "data-[side=top]:-bottom-2 data-[side=top]:rotate-180"),
					children: /* @__PURE__ */ jsx(ArrowTooltipSvg, { theme })
				}), body]
			})
		}) })]
	});
});
Tooltip.displayName = "Tooltip";
//#endregion
export { Tooltip };

//# sourceMappingURL=index.js.map